import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthLite } from "@/context/AuthLite";
import { apiUrl } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { selectionItemOutlineClasses } from "@/lib/selection-item-styles";
import { useIsMobile } from "@/hooks/use-mobile";
import Loading from "@/components/Loading";
import { FullPageMessage } from "@/components/FullPageMessage";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { SelectionLiveRegion } from "@/components/ui/selection-live-region";
import { SelectionToolbarLayout } from "@/components/ui/selection-toolbar-layout";
import { useSelectionKeyboardShortcuts } from "@/hooks/use-selection-keyboard-shortcuts";
import { FabMenu } from "@/components/ui/fab-menu";
import { hapticTap } from "@/lib/haptics";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AppSheet, AppSheetFooterButton } from "@/components/ui/app-sheet";
import { MobileSheet, MobileSheetFooterButton } from "@/components/ui/mobile-sheet";
import {
  Trash2,
  Plus,
  Folder,
  Search,
  X,
  Download,
  Loader2,
  Pencil,
  Edit,
  MoreVertical,
  Filter,
  AlertCircle,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import type { ImageFolder, ImageFolderFilters, ImageFolderCategory, SubfolderType } from "@shared/api";

type ProjektySortKey =
  | "name"
  | "brand"
  | "folder_type"
  | "model"
  | "panel_structure"
  | "color"
  | "prelis_type"
  | "install_date"
  | "deal"
  | "fb_upload_status"
  | "ig_upload_status";

type ProjektySortOrder = "asc" | "desc";

const PROJEKTY_SORT_FEATURE_KEY = "projekty-table-sort";
const PROJEKTY_SORT_KEYS = [
  "name",
  "brand",
  "folder_type",
  "model",
  "panel_structure",
  "color",
  "prelis_type",
  "install_date",
  "deal",
  "fb_upload_status",
  "ig_upload_status",
] as const satisfies readonly ProjektySortKey[];
const PROJEKTY_SORT_ORDERS = ["asc", "desc"] as const satisfies readonly ProjektySortOrder[];

function projektySortValue(folder: ImageFolder, key: ProjektySortKey): string {
  switch (key) {
    case "name":
      return folder.name || "";
    case "brand":
      return folder.brand || "";
    case "folder_type":
      return folder.folder_type || "";
    case "model":
      return folder.model || "";
    case "panel_structure":
      return folder.panel_structure || "";
    case "color":
      return folder.color || "";
    case "prelis_type":
      return folder.prelis_type || "";
    case "install_date":
      return folder.install_date || "";
    case "deal":
      return folder.deals?.title || "";
    case "fb_upload_status":
      return folder.fb_upload_status || "";
    case "ig_upload_status":
      return folder.ig_upload_status || "";
    default:
      return "";
  }
}

function compareProjektyFolders(
  a: ImageFolder,
  b: ImageFolder,
  key: ProjektySortKey,
  order: ProjektySortOrder,
): number {
  const av = projektySortValue(a, key).trim();
  const bv = projektySortValue(b, key).trim();
  const aEmpty = !av;
  const bEmpty = !bv;
  if (aEmpty && bEmpty) return a.id.localeCompare(b.id);
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
  if (cmp !== 0) return order === "asc" ? cmp : -cmp;
  return a.id.localeCompare(b.id);
}
import {
  filterImageFiles,
  isValidImageFile as isValidImageFileForDrop,
  dataTransferHasFilePayload,
} from "@/lib/filter-image-drop-files";
import { uploadImageFilesToCentralGallery } from "@/lib/upload-image-files-to-central-gallery";
import { uploadImages } from "@/lib/image-upload";
import { toast } from "@/hooks/use-toast";
import ImageFilters from "@/components/images/ImageFilters";
import FolderCard from "@/components/images/FolderCard";
import LookupSelect from "@/components/images/LookupSelect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { CheckSquare } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { readImageFiltersFromQuery, writeImageFiltersToQuery } from "@/lib/image-filter-query";
import { ImagesSectionTabs } from "@/components/images/ImagesSectionTabs";
import { useGalleryPhotoFacets } from "@/hooks/use-gallery-photo-facets";
import { SocialUploadStatusBadge } from "@/components/images/SocialUploadStatusBadge";
import {
  buildUserScopedKey,
  loadSortPrefV1,
  migrateAnonToUserScopedV1,
  saveSortPrefV1,
} from "@/lib/user-scoped-storage";

export default function ImagesPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuthLite();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  // Filter state (URL-synced)
  const initialFilterState = readImageFiltersFromQuery(searchParams);
  const [filters, setFilters] = useState<ImageFolderFilters>(initialFilterState.filters);
  const [searchName, setSearchName] = useState(initialFilterState.searchName);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(initialFilterState.dateFilterEnabled);

  useEffect(() => {
    const next = writeImageFiltersToQuery(searchParams, {
      filters,
      searchName,
      dateFilterEnabled,
    });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchName, dateFilterEnabled, searchParams, setSearchParams]);

  // Get min/max dates from image folders for date range slider
  const dateRangeQuery = useQuery({
    queryKey: ['image-folders-date-range'],
    queryFn: async () => {
      const r = await fetch(apiUrl('/api/images/folders/date-range'), {
        credentials: 'include',
      });
      if (!r.ok) return { minDate: undefined, maxDate: undefined };
      const data = await r.json();
      return {
        minDate: data.minDate as string | undefined, // YYYY-MM format
        maxDate: data.maxDate as string | undefined, // YYYY-MM format
      };
    },
  });

  // Manage automatic date range when the user enables/disables the filter
  useEffect(() => {
    if (dateFilterEnabled) {
      if (!dateRangeQuery.data?.minDate || !dateRangeQuery.data?.maxDate) {
        return;
      }
      setFilters((prev) => {
        const nextDateFrom = prev.dateFrom ?? dateRangeQuery.data!.minDate;
        const nextDateTo = prev.dateTo ?? dateRangeQuery.data!.maxDate;
        if (prev.dateFrom === nextDateFrom && prev.dateTo === nextDateTo) {
          return prev;
        }
        return {
          ...prev,
          dateFrom: nextDateFrom,
          dateTo: nextDateTo,
        };
      });
    } else {
      setFilters((prev) => {
        if (!prev.dateFrom && !prev.dateTo) {
          return prev;
        }
        const next = { ...prev };
        delete next.dateFrom;
        delete next.dateTo;
        return next;
      });
    }
  }, [dateFilterEnabled, dateRangeQuery.data?.minDate, dateRangeQuery.data?.maxDate]);

  // Selection state
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [selectionModeEnabled, setSelectionModeEnabled] = useState(false);
  const selectionMode = selectionModeEnabled;
  const desktopSelectionMode = !isMobile && selectionModeEnabled;
  const bulkHeaderDismissRef = useRef<HTMLButtonElement>(null);
  const longPressTimersRef = useRef<Map<string, number>>(new Map());
  const [socialMarkOpen, setSocialMarkOpen] = useState(false);
  const [socialUnmarkOpen, setSocialUnmarkOpen] = useState(false);
  const [socialMarkChannel, setSocialMarkChannel] = useState<"both" | "facebook" | "instagram">("both");
  const [socialMarkDate, setSocialMarkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [socialBulkPending, setSocialBulkPending] = useState(false);
  const [projektySortKey, setProjektySortKey] = useState<ProjektySortKey>("name");
  const [projektySortOrder, setProjektySortOrder] = useState<ProjektySortOrder>("asc");

  // Restore Projekty table sort (user-scoped localStorage; survives refresh)
  useEffect(() => {
    migrateAnonToUserScopedV1({ userEmail: user?.email, featureKey: PROJEKTY_SORT_FEATURE_KEY });
    const scopedKey = buildUserScopedKey(user?.email, PROJEKTY_SORT_FEATURE_KEY);
    const pref = loadSortPrefV1<ProjektySortKey, ProjektySortOrder>(
      scopedKey,
      PROJEKTY_SORT_KEYS,
      PROJEKTY_SORT_ORDERS,
      { sortBy: "name", sortOrder: "asc" },
    );
    setProjektySortKey(pref.sortBy);
    setProjektySortOrder(pref.sortOrder);
  }, [user?.email]);

  const toggleProjektySort = useCallback(
    (key: ProjektySortKey) => {
      const nextKey = key;
      const nextOrder: ProjektySortOrder =
        projektySortKey === key ? (projektySortOrder === "asc" ? "desc" : "asc") : "asc";
      setProjektySortKey(nextKey);
      setProjektySortOrder(nextOrder);
      saveSortPrefV1<ProjektySortKey, ProjektySortOrder>(
        buildUserScopedKey(user?.email, PROJEKTY_SORT_FEATURE_KEY),
        { sortBy: nextKey, sortOrder: nextOrder },
      );
    },
    [projektySortKey, projektySortOrder, user?.email],
  );

  const runSocialStatusBulk = useCallback(
    async (body: {
      project_ids: string[];
      facebook: "posted" | "not_posted" | null;
      instagram: "posted" | "not_posted" | null;
      posted_at: string | null;
      source: "manual";
    }) => {
      setSocialBulkPending(true);
      try {
        const r = await fetch(apiUrl("/api/images/folders/social-status/bulk"), {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          toast({
            title: t("common.error"),
            description: typeof data.error === "string" ? data.error : t("images.errorLoadFolders"),
            variant: "destructive",
          });
          return;
        }
        toast({
          title: t("imagesSocial.bulkToast", {
            updated: data.updated ?? 0,
            skipped: data.skipped ?? 0,
          }),
        });
        await qc.invalidateQueries({ queryKey: ["image-folders"] });
      } finally {
        setSocialBulkPending(false);
      }
    },
    [qc, t],
  );

  const handleSocialMarkSubmit = useCallback(async () => {
    const ids = Array.from(selectedFolders);
    if (ids.length === 0) return;
    const facebook =
      socialMarkChannel === "instagram" ? null : ("posted" as const);
    const instagram =
      socialMarkChannel === "facebook" ? null : ("posted" as const);
    await runSocialStatusBulk({
      project_ids: ids,
      facebook,
      instagram,
      posted_at: socialMarkDate || null,
      source: "manual",
    });
    setSocialMarkOpen(false);
  }, [selectedFolders, socialMarkChannel, socialMarkDate, runSocialStatusBulk]);

  const handleSocialUnmarkSubmit = useCallback(async () => {
    const ids = Array.from(selectedFolders);
    if (ids.length === 0) return;
    await runSocialStatusBulk({
      project_ids: ids,
      facebook: "not_posted",
      instagram: "not_posted",
      posted_at: null,
      source: "manual",
    });
    setSocialUnmarkOpen(false);
  }, [selectedFolders, runSocialStatusBulk]);
  const longPressStartRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  const exitFolderSelectionMode = useCallback(() => {
    hapticTap(12);
    setSelectionModeEnabled(false);
    setSelectedFolders(new Set());
  }, []);

  const beginFolderLongPress = useCallback(
    (folderId: string, e: React.PointerEvent) => {
      if (!isMobile) return;
      if (selectionModeEnabled) return;
      if (e.button !== 0) return;
      longPressStartRef.current.set(folderId, { x: e.clientX, y: e.clientY });
      const tId = window.setTimeout(() => {
        hapticTap(12);
        setSelectionModeEnabled(true);
        setSelectedFolders(new Set([folderId]));
      }, 420);
      longPressTimersRef.current.set(folderId, tId);
    },
    [isMobile, selectionModeEnabled],
  );

  const cancelFolderLongPress = useCallback((folderId: string) => {
    const tId = longPressTimersRef.current.get(folderId);
    if (tId != null) window.clearTimeout(tId);
    longPressTimersRef.current.delete(folderId);
    longPressStartRef.current.delete(folderId);
  }, []);

  const moveFolderLongPress = useCallback(
    (folderId: string, e: React.PointerEvent) => {
      const start = longPressStartRef.current.get(folderId);
      if (!start) return;
      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);
      if (dx > 12 || dy > 12) cancelFolderLongPress(folderId);
    },
    [cancelFolderLongPress],
  );

  // Mobil: szűrő panel (Sheet)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Create folder dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    panelStructure: "",
    color: "",
    prelisType: "",
    brand: "",
    folderType: "",
    model: "",
    installDate: "",
    dealId: "",
    category: "bemeres" as ImageFolderCategory,
  });

  // Edit folder dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    panelStructure: "",
    color: "",
    prelisType: "",
    brand: "",
    folderType: "",
    model: "",
    installDate: "",
    dealId: "",
    category: "bemeres" as ImageFolderCategory,
    socialCategory: "" as "" | "residential" | "industrial",
    socialAllowed: true,
    titlePublic: "",
    locationTown: "",
    quantity: "",
  });

  // Deal search state for create dialog
  const [createDealSearchOpen, setCreateDealSearchOpen] = useState(false);
  const [createDealSearchValue, setCreateDealSearchValue] = useState("");

  // Deal search state for edit dialog
  const [editDealSearchOpen, setEditDealSearchOpen] = useState(false);
  const [editDealSearchValue, setEditDealSearchValue] = useState("");

  // Drag & drop state for folder upload
  const [pendingFolderFiles, setPendingFolderFiles] = useState<Array<{ file: File; subfolderType: "munkaink" | "marketing" }>>([]);
  const [pendingFolderName, setPendingFolderName] = useState<string>("");

  const [imageDropDialogOpen, setImageDropDialogOpen] = useState(false);
  const [imageDropFiles, setImageDropFiles] = useState<File[]>([]);
  const [imageDropFolderId, setImageDropFolderId] = useState<string>("");
  const [imageDropSubfolder, setImageDropSubfolder] = useState<SubfolderType>("munkaink");
  const [imageDropAction, setImageDropAction] = useState<null | "folder" | "gallery">(null);
  const [fileDragHighlightProjects, setFileDragHighlightProjects] = useState(false);

  // Fetch deals for search - use the same query key as useGlobalSearch to share cache
  const boardQuery = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/board"), {
        credentials: "include",
      });
      if (!r.ok) {
        console.error("[Deals Query] Failed to fetch board:", r.status);
        throw new Error(`Board fetch failed with status ${r.status}`);
      }
      const data = await r.json();
      // Ensure deals is always an array
      const deals = Array.isArray(data.deals) ? data.deals : [];
      return {
        ...data,
        deals: deals, // Ensure deals is always an array
      };
    },
    retry: (failureCount, error) => {
      // Retry up to 3 times with exponential backoff
      if (failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, max 30s
    staleTime: 5 * 60 * 1000, // 5 minutes - same as useGlobalSearch
    // Don't cache errors - let React Query handle retries
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Extract deals from board data - ensure it's always an array
  const dealsQuery = {
    data: Array.isArray(boardQuery.data?.deals) ? boardQuery.data.deals : [],
    isLoading: boardQuery.isLoading,
    isError: boardQuery.isError,
    error: boardQuery.error,
  } as { data: Array<{ id: string; title: string; width?: string; height?: string; color?: string; surface?: string; emboss?: string; closingDate?: string }>; isLoading: boolean; isError: boolean; error: any };

  // Fetch folders
  const foldersQuery = useQuery({
    queryKey: ["image-folders", filters, searchName, dateFilterEnabled],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.panelStructure) {
        const values = Array.isArray(filters.panelStructure) ? filters.panelStructure : [filters.panelStructure];
        values.forEach(v => params.append("panelStructure", v));
      }
      if (filters.brand) {
        const values = Array.isArray(filters.brand) ? filters.brand : [filters.brand];
        values.forEach(v => params.append("brand", v));
      }
      if (filters.folderType) {
        const values = Array.isArray(filters.folderType) ? filters.folderType : [filters.folderType];
        values.forEach(v => params.append("folderType", v));
      }
      if (filters.model) {
        const values = Array.isArray(filters.model) ? filters.model : [filters.model];
        values.forEach(v => params.append("model", v));
      }
      if (filters.color) {
        const values = Array.isArray(filters.color) ? filters.color : [filters.color];
        values.forEach(v => params.append("color", v));
      }
      if (filters.prelisType) {
        const values = Array.isArray(filters.prelisType) ? filters.prelisType : [filters.prelisType];
        values.forEach(v => params.append("prelisType", v));
      }
      if (dateFilterEnabled && filters.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (dateFilterEnabled && filters.dateTo) params.set("dateTo", filters.dateTo);
      if (searchName) params.set("name", searchName);
      if (filters.category) {
        const values = Array.isArray(filters.category) ? filters.category : [filters.category];
        values.forEach(v => params.append("category", v));
      }
      if (filters.fbUploadStatus) {
        const values = Array.isArray(filters.fbUploadStatus) ? filters.fbUploadStatus : [filters.fbUploadStatus];
        values.forEach((v) => params.append("fbUploadStatus", v));
      }
      if (filters.igUploadStatus) {
        const values = Array.isArray(filters.igUploadStatus) ? filters.igUploadStatus : [filters.igUploadStatus];
        values.forEach((v) => params.append("igUploadStatus", v));
      }
      if (filters.socialPosted && filters.socialPosted !== "all") {
        params.set("socialPosted", filters.socialPosted);
      }

      const r = await fetch(apiUrl(`/api/images/folders?${params.toString()}`), {
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("images.errorLoadFolders"));
      const data = await r.json();
      return data.folders as ImageFolder[];
    },
    placeholderData: (previousData) => previousData, // Keep previous data visible while loading new data
  });

  useSelectionKeyboardShortcuts({
    enabled: selectionModeEnabled,
    onExit: exitFolderSelectionMode,
    onSelectAllInView: () => {
      const list = foldersQuery.data ?? [];
      if (list.length === 0) return;
      setSelectedFolders(new Set(list.map((f) => f.id)));
    },
  });

  useEffect(() => {
    if (!selectionModeEnabled) return;
    const id = requestAnimationFrame(() => bulkHeaderDismissRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [selectionModeEnabled]);

  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ id: string; name: string } | null>(null);

  // Delete folder mutation
  const deleteFolder = useMutation({
    mutationFn: async (folderId: string) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}`), {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(t("images.errorDeleteFolder"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folders"] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      setSelectedFolders(new Set());
      setDeleteConfirmOpen(false);
      setFolderToDelete(null);
    },
  });

  const handleDeleteClick = (folder: ImageFolder) => {
    setFolderToDelete({ id: folder.id, name: folder.name });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (folderToDelete) {
      deleteFolder.mutate(folderToDelete.id);
    }
  };

  // Bulk delete
  const bulkDelete = useMutation({
    mutationFn: async (folderIds: string[]) => {
      await Promise.all(folderIds.map((id) => deleteFolder.mutateAsync(id)));
    },
    onSuccess: () => {
      setSelectedFolders(new Set());
    },
  });

  const handleSelectFolder = (folderId: string, checked: boolean) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(folderId);
      } else {
        next.delete(folderId);
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedFolders.size === 0) return;
    if (confirm(`Biztosan törölni szeretnéd a kiválasztott ${selectedFolders.size} mappát? Ez a művelet nem vonható vissza.`)) {
      bulkDelete.mutate(Array.from(selectedFolders));
    }
  };

  // Helper function to read all files from a directory entry
  // Returns files with their subfolder type: "munkaink" for root folder files, "marketing" for subfolder files
  const readDirectoryEntries = async (
    directoryEntry: any, 
    path: string = "", 
    isSubfolder: boolean = false
  ): Promise<Array<{ file: File; subfolderType: "munkaink" | "marketing" }>> => {
    const files: Array<{ file: File; subfolderType: "munkaink" | "marketing" }> = [];
    const reader = directoryEntry.createReader();
    
    // Determine subfolder type: if we're in a subfolder (path is not empty), use "marketing", otherwise "munkaink"
    const subfolderType: "munkaink" | "marketing" = isSubfolder ? "marketing" : "munkaink";
    
    const readEntries = async (): Promise<void> => {
      return new Promise((resolve, reject) => {
        reader.readEntries(async (entries: any[]) => {
          if (entries.length === 0) {
            resolve();
            return;
          }
          
          for (const entry of entries) {
            if (entry.isDirectory) {
              // This is a subfolder, so mark it as such when reading its contents
              const subFiles = await readDirectoryEntries(entry, `${path}/${entry.name}`, true);
              files.push(...subFiles);
            } else {
              const file = await new Promise<File>((resolveFile, rejectFile) => {
                entry.file((file: File) => {
                  resolveFile(file);
                }, rejectFile);
              });
              
              // Only include valid image files
              if (isValidImageFileForDrop(file.name, file.type)) {
                files.push({ file, subfolderType });
              } else if (import.meta.env.DEV) {
                console.log(`[Images] Skipping non-image file: ${file.name}`);
              }
            }
          }
          
          // Continue reading if there are more entries
          const moreFiles = await readEntries();
          resolve();
        }, reject);
      });
    };
    
    await readEntries();
    return files;
  };

  // Handle folder drag and drop (directory → new folder wizard) or image files → target dialog
  const handleFolderDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFileDragHighlightProjects(false);

    const items = Array.from(e.dataTransfer.items);
    const folderEntries: any[] = [];

    for (const item of items) {
      if (item.kind === "file") {
        const entry = item.webkitGetAsEntry?.();
        if (entry && entry.isDirectory) {
          folderEntries.push(entry);
        }
      }
    }

    if (folderEntries.length > 0) {
      const folderEntry = folderEntries[0];
      const folderName = folderEntry.name;
      try {
        const files = await readDirectoryEntries(folderEntry);
        setPendingFolderName(folderName);
        setPendingFolderFiles(files);
        setCreateForm({
          ...createForm,
          name: folderName,
        });
        setShowCreateDialog(true);
      } catch (error) {
        console.error("Error reading folder:", error);
        alert(t("images.errorReadFolder"));
      }
      return;
    }

    const imageFiles = filterImageFiles(Array.from(e.dataTransfer.files));
    if (imageFiles.length === 0) return;

    const list = foldersQuery.data ?? [];
    setImageDropFiles(imageFiles);
    setImageDropFolderId(list[0]?.id ?? "");
    setImageDropSubfolder("munkaink");
    setImageDropDialogOpen(true);
  };

  const closeImageDropDialog = () => {
    setImageDropDialogOpen(false);
    setImageDropFiles([]);
  };

  const confirmImageDropToFolder = async () => {
    if (!imageDropFolderId || imageDropFiles.length === 0) return;
    setImageDropAction("folder");
    try {
      const res = await uploadImages({
        uploadUrl: apiUrl(`/api/images/folders/${imageDropFolderId}/photos`),
        files: imageDropFiles,
        extraFields: { subfolderType: imageDropSubfolder },
        context: { folderId: imageDropFolderId },
      });
      await qc.invalidateQueries({ queryKey: ["image-folders"] });
      await qc.invalidateQueries({ queryKey: ["image-folder", imageDropFolderId] });
      await qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      toast({
        title: t("images.imageDropFolderSuccess"),
        description:
          res.queued > 0
            ? t("uploadOutbox.partialSuccess", { queued: res.queued, ok: res.uploaded })
            : t("images.imageDropFolderSuccessDesc", { count: imageDropFiles.length }),
      });
      closeImageDropDialog();
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("images.imageDropError"),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setImageDropAction(null);
    }
  };

  const confirmImageDropToGallery = async () => {
    if (imageDropFiles.length === 0) return;
    setImageDropAction("gallery");
    try {
      const res = await uploadImageFilesToCentralGallery(imageDropFiles, qc);
      toast({
        title: t("images.imageDropGallerySuccess"),
        description:
          res.queued > 0 && res.uploaded === 0
            ? t("uploadOutbox.queuedBatch", { count: res.queued })
            : t("images.imageDropGallerySuccessDesc", { count: imageDropFiles.length }),
      });
      closeImageDropDialog();
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("images.imageDropError"),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setImageDropAction(null);
    }
  };

  // Create folder mutation
  const createFolder = useMutation({
    mutationFn: async (data: any) => {
      const r = await fetch(apiUrl("/api/images/folders"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(t("images.errorCreateFolder"));
      return r.json();
    },
    onSuccess: async (data) => {
      qc.invalidateQueries({ queryKey: ["image-folders"] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      
      // Upload pending files if any
      if (pendingFolderFiles.length > 0 && data.folder?.id) {
        try {
          // Group files by subfolder type for batch uploading
          const filesByType = {
            munkaink: pendingFolderFiles.filter(item => item.subfolderType === "munkaink"),
            marketing: pendingFolderFiles.filter(item => item.subfolderType === "marketing"),
          };
          
          const allUploadedPhotos: any[] = [];

          // Upload files grouped by subfolder type
          for (const [subfolderType, files] of Object.entries(filesByType)) {
            if (files.length === 0) continue;
            const res = await uploadImages({
              uploadUrl: apiUrl(`/api/images/folders/${data.folder.id}/photos`),
              files: files.map((item) => item.file),
              extraFields: { subfolderType },
              context: { folderId: data.folder.id },
            });
            allUploadedPhotos.push(...res.photos);
          }
          
          // Generate thumbnails for uploaded photos
          if (allUploadedPhotos.length > 0) {
            const { generateAndUploadThumbnail } = await import("@/lib/image-thumbnail");
            if (import.meta.env.DEV) {
              console.log(`[Thumbnail] Generating thumbnails for ${allUploadedPhotos.length} photos from folder upload`);
            }
            
            for (const photo of allUploadedPhotos) {
              if (!photo.thumbnail_url) {
                const matchingItem = pendingFolderFiles.find(item => item.file.name === photo.file_name);
                if (matchingItem) {
                  generateAndUploadThumbnail(
                    matchingItem.file,
                    data.folder.id,
                    matchingItem.subfolderType,
                    photo.id
                  ).catch((error) => {
                    console.error(`[Thumbnail] Error generating thumbnail for ${photo.file_name}:`, error);
                  });
                }
              }
            }
          }
        } catch (error) {
          console.error("Error uploading files:", error);
        }
        qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      }
      
      setShowCreateDialog(false);
      setCreateForm({
        name: "",
        panelStructure: "",
        color: "",
        prelisType: "",
        brand: "",
        folderType: "",
        model: "",
        installDate: "",
        dealId: "",
        category: "bemeres",
      });
      setPendingFolderFiles([]);
      setPendingFolderName("");
      navigate(`/images/${data.folder.id}`);
    },
    onError: (error) => {
      console.error("Error creating folder:", error);
      alert(t("images.errorCreateFolderAlert"));
    },
  });

  const handleCreateFolder = () => {
    setShowCreateDialog(true);
  };

  // Update folder mutation
  const updateFolder = useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: any }) => {
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(t("images.errorUpdateFolder"));
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["image-folders"] });
      qc.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      setShowEditDialog(false);
      setEditingFolderId(null);
    },
  });

  const handleEditFolder = (folder: ImageFolder) => {
    setEditingFolderId(folder.id);
    setEditForm({
      name: folder.name || "",
      panelStructure: folder.panel_structure || "",
      color: folder.color || "",
      prelisType: folder.prelis_type || "",
      brand: folder.brand || "",
      folderType: folder.folder_type || "",
      model: folder.model || "",
      installDate: folder.install_date || "",
      dealId: folder.deal_id || "",
      category: (folder.category && ["bemeres", "realizacio", "egyeb"].includes(folder.category) ? folder.category : "bemeres") as ImageFolderCategory,
      socialCategory:
        folder.social_category === "industrial" || folder.social_category === "residential"
          ? folder.social_category
          : "",
      socialAllowed: folder.social_allowed !== false,
      titlePublic: folder.title_public || "",
      locationTown: folder.location_town || "",
      quantity: folder.quantity != null ? String(folder.quantity) : "",
    });
    setShowEditDialog(true);
  };

  // Load deal data and populate form fields
  const loadDealData = async (dealId: string, isEdit: boolean = false) => {
    try {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), {
        credentials: "include",
      });
      if (!r.ok) {
        console.error("Failed to load deal:", r.status);
        return;
      }
      const data = await r.json();
      const deal = data.deal || data;

      // Map deal fields to form fields (same variables as in deals)
      const panelStructure = deal.surface || null; // Use surface directly, not width x height
      const color = deal.color || null;
      // Handle emboss field - extract prelis type from deal.emboss
      // Supports patterns like "M", "L", "W", "G", "Prelis M", "Prelis L", etc.
      let prelisType: string | undefined = undefined;
      if (deal.emboss) {
        const embossUpper = String(deal.emboss).toUpperCase().trim();
        // Match "PRELIS X" pattern first
        const prelisMatch = embossUpper.match(/PRELIS\s+([A-Z0-9]+)/);
        if (prelisMatch && prelisMatch[1]) {
          prelisType = prelisMatch[1];
        } else if (embossUpper.length <= 5 && /^[A-Z0-9]+$/.test(embossUpper)) {
          // If it's a short alphanumeric value (likely a prelis type), use it
          prelisType = embossUpper;
        }
      }
      const closingDate = deal.closingDate ? deal.closingDate.split("T")[0] : null; // Extract date part from ISO string

      if (isEdit) {
        setEditForm((prev) => ({
          ...prev,
          dealId: dealId,
          panelStructure: panelStructure || prev.panelStructure,
          color: color || prev.color,
          prelisType: prelisType || prev.prelisType,
          installDate: closingDate || prev.installDate,
        }));
      } else {
        setCreateForm((prev) => ({
          ...prev,
          dealId: dealId,
          panelStructure: panelStructure || prev.panelStructure,
          color: color || prev.color,
          prelisType: prelisType || prev.prelisType,
          installDate: closingDate || prev.installDate,
        }));
      }
    } catch (error) {
      console.error("Error loading deal data:", error);
    }
  };

  const folders = foldersQuery.data || [];
  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => compareProjektyFolders(a, b, projektySortKey, projektySortOrder)),
    [folders, projektySortKey, projektySortOrder],
  );
  const galleryFacetsQuery = useGalleryPhotoFacets();
  const filterOptionCounts = galleryFacetsQuery.data;

  if (foldersQuery.isLoading) {
    return (
      <>
        {/* ux-consistency: loading/empty C */}
        <Loading label={t("imagesExtra.loadingDot")} className="min-h-[50vh]" />
      </>
    );
  }

  if (foldersQuery.isError) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
        <div className="relative z-20 flex h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
          <PageTitleBar
            className="h-full border-0"
            rowClassName="h-full min-h-0 items-center"
            title={t("images.title")}
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <FullPageMessage
            variant="embedded"
            icon={AlertCircle}
            title={t("crm.loadError")}
            subtitle={t("common.tryAgainLater")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 min-h-[60px] h-[60px] px-3 py-3 sm:px-4 sm:py-4 relative overflow-hidden bg-background border-b z-20">
        {/* Normal Header – csak cím + akciók, mint a RouteGenerator felső sora */}
        <div
          className={cn(
            "absolute inset-0 flex items-center transition-all duration-300 ease-in-out",
            desktopSelectionMode
              ? "opacity-0 translate-y-[-100%] pointer-events-none"
              : "opacity-100 translate-y-0",
          )}
        >
          <PageTitleBar
            className="w-full min-w-0 border-0"
            rowClassName="min-h-0 flex-1 items-center"
            title={t("images.title")}
            right={
              <>
                {isMobile ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {(foldersQuery.data ?? []).length > 0 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 shrink-0 p-0"
                        type="button"
                        aria-label={selectionModeEnabled ? t("images.cancelSelection") : t("images.selectionShort")}
                        onClick={() => {
                          hapticTap(12);
                          if (selectionModeEnabled) exitFolderSelectionMode();
                          else setSelectionModeEnabled(true);
                        }}
                      >
                        {selectionModeEnabled ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                      </Button>
                    ) : null}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 shrink-0 p-0"
                      onClick={() => setFilterSheetOpen(true)}
                      aria-label={t("common.filterLabel")}
                    >
                      <Filter className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null}
                {!isMobile ? (
                  <div className="flex items-center gap-1 md:gap-2">
                    {!selectionModeEnabled ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="px-2 text-xs md:px-3 md:text-sm"
                        onClick={() => setSelectionModeEnabled(true)}
                      >
                        {t("common.selectTrigger")}
                      </Button>
                    ) : null}
                    <Button onClick={handleCreateFolder} size="sm" className="px-2 text-xs md:px-3 md:text-sm">
                      <Plus className="mr-2 h-4 w-4" />
                      {t("images.newFolder")}
                    </Button>
                  </div>
                ) : null}
              </>
            }
          />
        </div>

        {/* Desktop Toolbar - slides up during selection */}
        {!isMobile ? (
          <div
            className={cn(
              "absolute inset-0 flex items-center justify-between gap-2 px-3 sm:px-4 transition-all duration-300 ease-in-out",
              desktopSelectionMode ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0",
            )}
          >
            <SelectionToolbarLayout
              closeButtonRef={bulkHeaderDismissRef}
              className="px-0 sm:px-0"
              selectedCount={selectedFolders.size}
              onClose={exitFolderSelectionMode}
              onSelectAll={() => {
                const list = foldersQuery.data ?? [];
                setSelectedFolders(new Set(list.map((f) => f.id)));
              }}
              selectAllDisabled={(foldersQuery.data ?? []).length === 0}
              bulkActions={
                selectedFolders.size > 0 ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="px-2 text-xs md:px-3 md:text-sm"
                      disabled={socialBulkPending}
                      onClick={() => {
                        setSocialMarkDate(new Date().toISOString().slice(0, 10));
                        setSocialMarkChannel("both");
                        setSocialMarkOpen(true);
                      }}
                    >
                      {t("imagesSocial.markAsUploaded")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="px-2 text-xs md:px-3 md:text-sm"
                      disabled={socialBulkPending}
                      onClick={() => setSocialUnmarkOpen(true)}
                    >
                      {t("imagesSocial.unmarkUploaded")}
                    </Button>
                  </>
                ) : null
              }
              rightActions={
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2 text-xs md:px-3 md:text-sm"
                  disabled={selectedFolders.size === 0}
                  onClick={async () => {
                    const folderIds = Array.from(selectedFolders);
                    for (const folderId of folderIds) {
                      try {
                        const r = await fetch(apiUrl(`/api/images/folders/${folderId}/download`), {
                          credentials: "include",
                        });
                        if (!r.ok) continue;
                        const data = await r.json();
                        data.photos?.forEach((photo: { url: string; fileName: string }) => {
                          window.open(apiUrl(photo.url), "_blank");
                        });
                      } catch {
                        // ignore
                      }
                    }
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t("images.downloadLabel")}
                </Button>
              }
              onDelete={handleBulkDelete}
              deleteDisabled={selectedFolders.size === 0}
              deletePending={bulkDelete.isPending}
            />
          </div>
        ) : null}
      </div>

      {/* Content with Sidebar */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[360px,1fr] gap-3 md:gap-6 h-full">
          {/* Sidebar */}
          <div className="hidden md:block overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Search Card */}
              <div className="p-4 border rounded-md bg-card space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={t("images.searchPlaceholder")}
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Filters Card */}
              <div className="p-4 border rounded-md bg-card">
                <ImageFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  optionCounts={filterOptionCounts}
                  minDate={dateRangeQuery.data?.minDate}
                  maxDate={dateRangeQuery.data?.maxDate}
                  dateFilterEnabled={dateFilterEnabled}
                  onDateFilterToggle={setDateFilterEnabled}
                  hideFiltersTitle
                />
              </div>
            </div>
          </div>

          {/* Main Content – mint ReverseRouteGeneratorPanel: egy görgető, benne container + TabsList + mt-4 */}
          <div
            className={cn(
              "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto",
              fileDragHighlightProjects && "ring-2 ring-primary ring-inset"
            )}
            onDragEnter={(e) => {
              if (!dataTransferHasFilePayload(e.dataTransfer)) return;
              e.preventDefault();
              setFileDragHighlightProjects(true);
            }}
            onDragLeave={(e) => {
              if (!dataTransferHasFilePayload(e.dataTransfer)) return;
              const next = e.relatedTarget as Node | null;
              if (next && (e.currentTarget as Node).contains(next)) return;
              setFileDragHighlightProjects(false);
            }}
            onDragOver={(e) => {
              if (!dataTransferHasFilePayload(e.dataTransfer)) return;
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={handleFolderDrop}
          >
            {fileDragHighlightProjects && (
              <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
                <p className="rounded-lg border bg-card px-4 py-3 text-center text-sm font-medium shadow-lg">
                  {t("images.dropReleaseHint")}
                </p>
              </div>
            )}
            <div className="container mx-auto w-full px-2 py-3 md:px-4 md:py-6">
              <ImagesSectionTabs active="folders" compact={isMobile} />
              <div className="mt-4">
        {folders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8">
            <Folder className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg">{t("images.noFolders")}</p>
            <p className="text-sm mt-2">{t("images.noFoldersSub")}</p>
          </div>
        ) : (
          <div className="p-6">
            {!isMobile && (
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <span className="text-sm text-foreground/60 font-medium">{t("images.type")}</span>
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-4 rounded-sm shrink-0 bg-green-500" aria-hidden />
                  <span>{t("images.realizacio")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-4 rounded-sm shrink-0 bg-red-500" aria-hidden />
                  <span>{t("images.bemeres")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-4 rounded-sm shrink-0 bg-slate-400" aria-hidden />
                  <span>{t("images.egyeb")}</span>
                </div>
              </div>
              <Select
                value={filters.category ? (Array.isArray(filters.category) ? filters.category[0] : filters.category) : "all"}
                onValueChange={(value) => setFilters(prev => ({ ...prev, category: value === "all" ? undefined : (value as ImageFolderCategory) }))}
              >
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue placeholder={t("images.categoryPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("imagesExtra.allCategoryOption")}</SelectItem>
                  <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                  <SelectItem value="realizacio">{t("images.realizacio")}</SelectItem>
                  <SelectItem value="egyeb">{t("images.egyeb")}</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.socialPosted || "all"}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    socialPosted:
                      value === "all" ? undefined : (value as "posted" | "unposted"),
                  }))
                }
              >
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder={t("imagesSocial.filterSocial")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("imagesSocial.filterAll")}</SelectItem>
                  <SelectItem value="posted">{t("imagesSocial.filterUploaded")}</SelectItem>
                  <SelectItem value="unposted">{t("imagesSocial.filterNotYet")}</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={
                  filters.fbUploadStatus
                    ? Array.isArray(filters.fbUploadStatus)
                      ? filters.fbUploadStatus[0]
                      : filters.fbUploadStatus
                    : "all"
                }
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    fbUploadStatus: value === "all" ? undefined : value,
                  }))
                }
              >
                <SelectTrigger className="w-[160px] h-8">
                  <SelectValue placeholder={t("imagesSocial.filterFb")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("imagesSocial.filterAll")}</SelectItem>
                  <SelectItem value="posted">{t("imagesSocial.filterUploaded")}</SelectItem>
                  <SelectItem value="partial">{t("imagesSocial.filterPartial")}</SelectItem>
                  <SelectItem value="failed">{t("imagesSocial.filterFailed")}</SelectItem>
                  <SelectItem value="none">{t("imagesSocial.filterNotYet")}</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={
                  filters.igUploadStatus
                    ? Array.isArray(filters.igUploadStatus)
                      ? filters.igUploadStatus[0]
                      : filters.igUploadStatus
                    : "all"
                }
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    igUploadStatus: value === "all" ? undefined : value,
                  }))
                }
              >
                <SelectTrigger className="w-[160px] h-8">
                  <SelectValue placeholder={t("imagesSocial.filterIg")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("imagesSocial.filterAll")}</SelectItem>
                  <SelectItem value="posted">{t("imagesSocial.filterUploaded")}</SelectItem>
                  <SelectItem value="failed">{t("imagesSocial.filterFailed")}</SelectItem>
                  <SelectItem value="none">{t("imagesSocial.filterNotYet")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {selectionModeEnabled && (
                      <TableHead className="w-12">
                        <Checkbox
                          aria-label={t("imagesSocial.selectAllOnPage")}
                          checked={
                            sortedFolders.length > 0 &&
                            sortedFolders.every((f) => selectedFolders.has(f.id))
                          }
                          onCheckedChange={(checked) => {
                            setSelectedFolders((prev) => {
                              const next = new Set(prev);
                              if (checked) {
                                sortedFolders.forEach((f) => next.add(f.id));
                              } else {
                                sortedFolders.forEach((f) => next.delete(f.id));
                              }
                              return next;
                            });
                          }}
                        />
                      </TableHead>
                    )}
                    {(
                      [
                        ["name", "min-w-[200px]", t("imagesExtra.folderNameHead")],
                        ["brand", "w-[120px]", t("imagesExtra.brandLabel")],
                        ["folder_type", "w-[140px]", t("imagesExtra.typeLabel")],
                        ["model", "w-[100px]", t("imagesExtra.modelLabel")],
                        ["panel_structure", "w-[120px]", t("imagesExtra.surfaceLabel")],
                        ["color", "w-[70px]", t("imagesExtra.colorLabel")],
                        ["prelis_type", "w-[100px]", t("imagesExtra.prelisLabel")],
                        ["install_date", "w-[130px]", t("imagesExtra.installDateHead")],
                        ["deal", "min-w-[150px]", t("imagesExtra.dealHead")],
                        ["fb_upload_status", "w-[140px]", t("imagesSocial.colFb")],
                        ["ig_upload_status", "w-[140px]", t("imagesSocial.colIg")],
                      ] as const
                    ).map(([key, widthClass, label]) => {
                      const active = projektySortKey === key;
                      return (
                        <TableHead key={key} className={widthClass}>
                          <button
                            type="button"
                            className={cn(
                              "inline-flex max-w-full items-center gap-1 text-left font-medium hover:text-foreground",
                              active ? "text-foreground" : "text-muted-foreground",
                            )}
                            onClick={() => toggleProjektySort(key)}
                          >
                            <span className="truncate">{label}</span>
                            {active ? (
                              projektySortOrder === "asc" ? (
                                <ArrowUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              ) : (
                                <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              )
                            ) : null}
                          </button>
                        </TableHead>
                      );
                    })}
                    <TableHead className="w-[120px]">{t("images.actionsLabel")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFolders.map((folder) => {
                    const isSelected = selectedFolders.has(folder.id);
                    const counts = folder.photo_counts ?? (folder as { photoCounts?: typeof folder.photo_counts }).photoCounts ?? { bemeres: 0, munkaink: 0, marketing: 0 };
                    const bemeres = (Number(counts.bemeres) ?? Number((counts as Record<string, unknown>).bemeres)) || 0;
                    const munkaink = (Number(counts.munkaink) ?? Number((counts as Record<string, unknown>).munkaink)) || 0;
                    const categoryStripClass =
                      munkaink > 0
                        ? "border-l-green-500"
                        : bemeres > 0
                          ? "border-l-red-500"
                          : "border-l-slate-400";
                    const stripType = munkaink > 0 ? "munkaink" : bemeres > 0 ? "bemeres" : "egyeb";
                    return (
                      <TableRow
                        key={folder.id}
                        data-folder-id={folder.id}
                        data-strip-type={stripType}
                        data-bemeres={bemeres}
                        data-munkaink={munkaink}
                        className={cn(
                          "cursor-pointer h-12",
                          selectionModeEnabled
                            ? selectionItemOutlineClasses(true, isSelected, { denseSelectedBg: true })
                            : cn("border-l-4", categoryStripClass),
                        )}
                        onPointerDown={(e) => beginFolderLongPress(folder.id, e)}
                        onPointerMove={(e) => moveFolderLongPress(folder.id, e)}
                        onPointerUp={() => cancelFolderLongPress(folder.id)}
                        onPointerCancel={() => cancelFolderLongPress(folder.id)}
                        onClick={() => {
                          if (selectionModeEnabled) {
                            handleSelectFolder(folder.id, !isSelected);
                          } else {
                            navigate(`/images/${folder.id}`);
                          }
                        }}
                      >
                        {selectionModeEnabled && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectFolder(folder.id, checked as boolean)}
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          <div className="flex items-start gap-2">
                            <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-2 break-words">{folder.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="truncate">{folder.brand || "-"}</TableCell>
                        <TableCell className="truncate">{folder.folder_type || "-"}</TableCell>
                        <TableCell className="truncate">{folder.model || "-"}</TableCell>
                        <TableCell className="truncate">{folder.panel_structure || "-"}</TableCell>
                        <TableCell className="truncate">{folder.color || "-"}</TableCell>
                        <TableCell className="truncate">{folder.prelis_type || "-"}</TableCell>
                        <TableCell className="truncate">{folder.install_date || "-"}</TableCell>
                        <TableCell className="truncate">{folder.deals?.title || "-"}</TableCell>
                        <TableCell className="align-top">
                          <SocialUploadStatusBadge kind="facebook" folder={folder} />
                        </TableCell>
                        <TableCell className="align-top">
                          <SocialUploadStatusBadge kind="instagram" folder={folder} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const r = await fetch(apiUrl(`/api/images/folders/${folder.id}/download-folder`), {
                                    credentials: "include",
                                  });
                                  if (!r.ok) {
                                    console.error(`Failed to download folder ${folder.id}`);
                                    return;
                                  }
                                  const blob = await r.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${folder.name}.zip`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error(`Error downloading folder ${folder.id}:`, error);
                                }
                              }}
                              title={t("images.downloadLabel")}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {user?.role === 'Admin' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    title={t("images.actionsLabel")}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditFolder(folder);
                                  }}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Szerkesztés
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(folder);
                                    }}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Törlés
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Folder Dialog */}
      {isMobile ? (
        <MobileSheet
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          title={t("imagesExtra.newFolderTitle")}
          description={t("imagesExtra.newFolderDescription")}
          footer={
            <>
              <MobileSheetFooterButton
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  setPendingFolderFiles([]);
                  setPendingFolderName("");
                }}
              >
                {t("common.cancel")}
              </MobileSheetFooterButton>
              <MobileSheetFooterButton
                onClick={() => {
                  if (!createForm.name.trim()) {
                    alert("A mappa neve kötelező");
                    return;
                  }
                  createFolder.mutate({
                    name: createForm.name,
                    panelStructure: createForm.panelStructure,
                    brand: createForm.brand,
                    folderType: createForm.folderType,
                    model: createForm.model,
                    color: createForm.color,
                    prelisType: createForm.prelisType,
                    dealId: createForm.dealId || undefined,
                    installDate: createForm.installDate || undefined,
                    category: createForm.category,
                  });
                }}
                disabled={createFolder.isPending}
              >
                {createFolder.isPending ? t("images.creating") : t("imagesExtra.createFolder")}
              </MobileSheetFooterButton>
            </>
          }
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">{t("imagesExtra.folderNameLabel")}</Label>
              <Input
                id="create-name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder={t("images.folderNamePlaceholder")}
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <LookupSelect
                  id="create-panelStructure"
                  type="panel_structure"
                  value={createForm.panelStructure}
                  onValueChange={(value) => setCreateForm({ ...createForm, panelStructure: value })}
                  label={t("imagesExtra.surfaceLabel")}
                  placeholder={t("images.dimensionsPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="create-brand"
                  type="brand"
                  value={createForm.brand}
                  onValueChange={(value) => setCreateForm({ ...createForm, brand: value })}
                  label={t("imagesExtra.brandLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="create-folderType"
                  type="folder_type"
                  value={createForm.folderType}
                  onValueChange={(value) => setCreateForm({ ...createForm, folderType: value || "" })}
                  label={t("imagesExtra.typeLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="create-model"
                  type="model"
                  value={createForm.model}
                  onValueChange={(value) => setCreateForm({ ...createForm, model: value || "" })}
                  label={t("imagesExtra.modelLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="create-color"
                  type="color"
                  value={createForm.color}
                  onValueChange={(value) => setCreateForm({ ...createForm, color: value })}
                  label={t("imagesExtra.colorLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="create-prelisType"
                  type="prelis"
                  value={createForm.prelisType}
                  onValueChange={(value) => setCreateForm({ ...createForm, prelisType: value || "" })}
                  label={t("imagesExtra.prelisLabel")}
                />
              </div>
              <div className="space-y-2 overflow-hidden">
                <Label htmlFor="create-dealId">{t("imagesExtra.dealLabel")}</Label>
                <Popover open={createDealSearchOpen} onOpenChange={setCreateDealSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={createDealSearchOpen}
                      className="w-full justify-between"
                    >
                      {createForm.dealId
                        ? (Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === createForm.dealId)?.title : null) || t("imagesExtra.dealSelected")
                        : t("imagesExtra.selectDealPlaceholder")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t("images.searchDealPlaceholder")}
                        value={createDealSearchValue}
                        onValueChange={setCreateDealSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>{t("imagesExtra.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {(() => {
                            const deals = Array.isArray(dealsQuery.data) ? dealsQuery.data : [];
                            const searchLower = createDealSearchValue.toLowerCase();
                            const filtered = deals.filter((deal) => {
                              const title = deal.title?.toLowerCase() || "";
                              const matches = title.includes(searchLower);
                              return matches;
                            });
                            return filtered;
                          })().map((deal) => (
                              <CommandItem
                                key={deal.id}
                                value={deal.id}
                                onSelect={() => {
                                  setCreateForm({ ...createForm, dealId: deal.id });
                                  setCreateDealSearchOpen(false);
                                  setCreateDealSearchValue("");
                                  loadDealData(deal.id, false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    createForm.dealId === deal.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {deal.title}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {createForm.dealId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCreateForm({ ...createForm, dealId: "" });
                    }}
                    className="mt-1"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Deal eltávolítása
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-installDate">{t("imagesExtra.installDateLabel")}</Label>
                <DatePickerPopover
                  id="create-installDate"
                  value={createForm.installDate}
                  onChange={(v) => setCreateForm({ ...createForm, installDate: v })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-category">{t("imagesExtra.categoryLabel")}</Label>
                <Select
                  value={createForm.category}
                  onValueChange={(value: ImageFolderCategory) => setCreateForm({ ...createForm, category: value })}
                >
                  <SelectTrigger id="create-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                    <SelectItem value="realizacio">{t("images.realizacio")}</SelectItem>
                    <SelectItem value="egyeb">{t("images.egyeb")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {pendingFolderFiles.length > 0 && (
              <div className="space-y-2 overflow-hidden">
                <Label>{t("imagesExtra.draggedFilesLabel", { count: pendingFolderFiles.length })}</Label>
                <div className="max-h-40 overflow-y-auto overflow-x-hidden border rounded-lg p-2 bg-muted/30">
                  <div className="space-y-1">
                    {pendingFolderFiles.slice(0, 10).map((item, index) => (
                      <div key={index} className="text-sm text-muted-foreground truncate flex items-center gap-2">
                        <span>{item.file.name}</span>
                        <span className="text-xs text-primary">({item.subfolderType === "munkaink" ? "Munkáink" : "Marketing"})</span>
                      </div>
                    ))}
                    {pendingFolderFiles.length > 10 && (
                      <div className="text-sm text-muted-foreground italic">
                        ... és még {pendingFolderFiles.length - 10} fájl
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  A fő mappában lévő fájlok a "Munkáink" szekcióba, az almappákban lévő fájlok pedig a "Marketing" szekcióba kerülnek automatikusan a mappa létrehozása után.
                </p>
              </div>
            )}
          </div>
        </MobileSheet>
      ) : (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden w-full max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("imagesExtra.newFolderTitle")}</DialogTitle>
                <DialogDescription>
                  {t("imagesExtra.newFolderDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 min-w-0 overflow-hidden">
                <div className="space-y-2 min-w-0">
                  <Label htmlFor="create-name">{t("imagesExtra.folderNameLabel")}</Label>
                  <Input
                    id="create-name"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder={t("images.folderNamePlaceholder")}
                    className="min-w-0 max-w-full"
                  />
                </div>
                <div className={cn("grid gap-4 min-w-0", "grid-cols-2")}>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-panelStructure"
                      type="panel_structure"
                      value={createForm.panelStructure}
                      onValueChange={(value) => setCreateForm({ ...createForm, panelStructure: value })}
                      label={t("imagesExtra.surfaceLabel")}
                      placeholder={t("images.dimensionsPlaceholder")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-brand"
                      type="brand"
                      value={createForm.brand}
                      onValueChange={(value) => setCreateForm({ ...createForm, brand: value })}
                      label={t("imagesExtra.brandLabel")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-folderType"
                      type="folder_type"
                      value={createForm.folderType}
                      onValueChange={(value) => setCreateForm({ ...createForm, folderType: value || "" })}
                      label={t("imagesExtra.typeLabel")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-model"
                      type="model"
                      value={createForm.model}
                      onValueChange={(value) => setCreateForm({ ...createForm, model: value || "" })}
                      label={t("imagesExtra.modelLabel")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-color"
                      type="color"
                      value={createForm.color}
                      onValueChange={(value) => setCreateForm({ ...createForm, color: value })}
                      label={t("imagesExtra.colorLabel")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <LookupSelect
                      id="create-prelisType"
                      type="prelis"
                      value={createForm.prelisType}
                      onValueChange={(value) => setCreateForm({ ...createForm, prelisType: value || "" })}
                      label={t("imagesExtra.prelisLabel")}
                    />
                  </div>
                  <div className="space-y-2 min-w-0 overflow-hidden">
                    <Label htmlFor="create-dealId">{t("imagesExtra.dealLabel")}</Label>
                    <Popover open={createDealSearchOpen} onOpenChange={setCreateDealSearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={createDealSearchOpen}
                          className="w-full justify-between"
                        >
                          {createForm.dealId
                            ? (Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === createForm.dealId)?.title : null) || t("imagesExtra.dealSelected")
                            : t("imagesExtra.selectDealPlaceholder")}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t("images.searchDealPlaceholder")}
                            value={createDealSearchValue}
                            onValueChange={setCreateDealSearchValue}
                          />
                          <CommandList>
                            <CommandEmpty>{t("imagesExtra.noResults")}</CommandEmpty>
                            <CommandGroup>
                              {(() => {
                                const deals = Array.isArray(dealsQuery.data) ? dealsQuery.data : [];
                                const searchLower = createDealSearchValue.toLowerCase();
                                const filtered = deals.filter((deal) => {
                                  const title = deal.title?.toLowerCase() || "";
                                  const matches = title.includes(searchLower);
                                  return matches;
                                });
                                return filtered;
                              })().map((deal) => (
                                  <CommandItem
                                    key={deal.id}
                                    value={deal.id}
                                    onSelect={() => {
                                      setCreateForm({ ...createForm, dealId: deal.id });
                                      setCreateDealSearchOpen(false);
                                      setCreateDealSearchValue("");
                                      loadDealData(deal.id, false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        createForm.dealId === deal.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {deal.title}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {createForm.dealId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCreateForm({ ...createForm, dealId: "" });
                        }}
                        className="mt-1"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Deal eltávolítása
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="create-installDate">{t("imagesExtra.installDateLabel")}</Label>
                    <DatePickerPopover
                      id="create-installDate"
                      value={createForm.installDate}
                      onChange={(v) => setCreateForm({ ...createForm, installDate: v })}
                      className="min-w-0 max-w-full"
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="create-category">{t("imagesExtra.categoryLabel")}</Label>
                    <Select
                      value={createForm.category}
                      onValueChange={(value: ImageFolderCategory) => setCreateForm({ ...createForm, category: value })}
                    >
                      <SelectTrigger id="create-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                        <SelectItem value="realizacio">{t("images.realizacio")}</SelectItem>
                        <SelectItem value="egyeb">{t("images.egyeb")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {pendingFolderFiles.length > 0 && (
                  <div className="space-y-2 min-w-0 overflow-hidden">
                    <Label>{t("imagesExtra.draggedFilesLabel", { count: pendingFolderFiles.length })}</Label>
                    <div className="max-h-40 overflow-y-auto overflow-x-hidden border rounded-lg p-2 bg-muted/30 min-w-0">
                      <div className="space-y-1">
                        {pendingFolderFiles.slice(0, 10).map((item, index) => (
                          <div key={index} className="text-sm text-muted-foreground truncate flex items-center gap-2">
                            <span>{item.file.name}</span>
                            <span className="text-xs text-primary">({item.subfolderType === "munkaink" ? "Munkáink" : "Marketing"})</span>
                          </div>
                        ))}
                        {pendingFolderFiles.length > 10 && (
                          <div className="text-sm text-muted-foreground italic">
                            ... és még {pendingFolderFiles.length - 10} fájl
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      A fő mappában lévő fájlok a "Munkáink" szekcióba, az almappákban lévő fájlok pedig a "Marketing" szekcióba kerülnek automatikusan a mappa létrehozása után.
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowCreateDialog(false);
                  setPendingFolderFiles([]);
                  setPendingFolderName("");
                }}>
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={() => {
                    if (!createForm.name.trim()) {
                      alert("A mappa neve kötelező");
                      return;
                    }
                    createFolder.mutate({
                      name: createForm.name.trim(),
                      panelStructure: createForm.panelStructure || null,
                      color: createForm.color || null,
                      prelisType: createForm.prelisType && createForm.prelisType.trim() ? createForm.prelisType : null,
                      brand: createForm.brand || null,
                      folderType: createForm.folderType || null,
                      model: createForm.model || null,
                      installDate: createForm.installDate || null,
                      dealId: createForm.dealId || null,
                      category: createForm.category || "bemeres",
                    });
                  }}
                  disabled={createFolder.isPending || !createForm.name.trim()}
                >
                  {createFolder.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("manuals.creatingButton")}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {t("manuals.createButton")}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

      {/* Edit Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("imagesExtra.editFolderTitle")}</DialogTitle>
            <DialogDescription>
              Módosítsa a mappa adatait és metadata mezőit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t("imagesExtra.folderNameLabel")}</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <LookupSelect
                  id="edit-panelStructure"
                  type="panel_structure"
                  value={editForm.panelStructure}
                  onValueChange={(value) => setEditForm({ ...editForm, panelStructure: value })}
                  label={t("imagesExtra.surfaceLabel")}
                  placeholder={t("images.dimensionsPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-brand"
                  type="brand"
                  value={editForm.brand}
                  onValueChange={(value) => setEditForm({ ...editForm, brand: value })}
                  label={t("imagesExtra.brandLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-folderType"
                  type="folder_type"
                  value={editForm.folderType}
                  onValueChange={(value) => setEditForm({ ...editForm, folderType: value || "" })}
                  label={t("imagesExtra.typeLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-model"
                  type="model"
                  value={editForm.model}
                  onValueChange={(value) => setEditForm({ ...editForm, model: value || "" })}
                  label={t("imagesExtra.modelLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-color"
                  type="color"
                  value={editForm.color}
                  onValueChange={(value) => setEditForm({ ...editForm, color: value })}
                  label={t("imagesExtra.colorLabel")}
                />
              </div>
              <div className="space-y-2">
                <LookupSelect
                  id="edit-prelisType"
                  type="prelis"
                  value={editForm.prelisType}
                  onValueChange={(value) => setEditForm({ ...editForm, prelisType: value || "" })}
                  label={t("imagesExtra.prelisLabel")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-dealId">{t("imagesExtra.dealLabel")}</Label>
                <Popover open={editDealSearchOpen} onOpenChange={setEditDealSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={editDealSearchOpen}
                      className="w-full justify-between"
                    >
                      {editForm.dealId
                        ? (Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === editForm.dealId)?.title : null) || t("imagesExtra.dealSelected")
                        : t("imagesExtra.selectDealPlaceholder")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t("images.searchDealPlaceholder")}
                        value={editDealSearchValue}
                        onValueChange={setEditDealSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>{t("imagesExtra.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {(() => {
                            const deals = Array.isArray(dealsQuery.data) ? dealsQuery.data : [];
                            const searchLower = editDealSearchValue.toLowerCase();
                            const filtered = deals.filter((deal) => {
                              const title = deal.title?.toLowerCase() || "";
                              const matches = title.includes(searchLower);
                              return matches;
                            });
                            return filtered;
                          })().map((deal) => (
                              <CommandItem
                                key={deal.id}
                                value={deal.id}
                                onSelect={() => {
                                  setEditForm({ ...editForm, dealId: deal.id });
                                  setEditDealSearchOpen(false);
                                  setEditDealSearchValue("");
                                  loadDealData(deal.id, true);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    editForm.dealId === deal.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {deal.title}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {editForm.dealId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditForm({ ...editForm, dealId: "" });
                    }}
                    className="mt-1"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Deal eltávolítása
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-installDate">{t("imagesExtra.installDateLabel")}</Label>
                <DatePickerPopover
                  id="edit-installDate"
                  value={editForm.installDate}
                  onChange={(v) => setEditForm({ ...editForm, installDate: v })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">{t("imagesExtra.categoryLabel")}</Label>
                <Select
                  value={editForm.category}
                  onValueChange={(value: ImageFolderCategory) => setEditForm({ ...editForm, category: value })}
                >
                  <SelectTrigger id="edit-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                    <SelectItem value="realizacio">{t("images.realizacio")}</SelectItem>
                    <SelectItem value="egyeb">{t("images.egyeb")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-socialCategory">{t("imagesSocial.referenceCategory")}</Label>
                <Select
                  value={editForm.socialCategory || "unset"}
                  onValueChange={(value) =>
                    setEditForm({
                      ...editForm,
                      socialCategory: value === "unset" ? "" : (value as "residential" | "industrial"),
                    })
                  }
                >
                  <SelectTrigger id="edit-socialCategory">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">{t("imagesSocial.referenceUnset")}</SelectItem>
                    <SelectItem value="residential">{t("imagesSocial.referenceResidential")}</SelectItem>
                    <SelectItem value="industrial">{t("imagesSocial.referenceIndustrial")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-titlePublic">{t("imagesSocial.titlePublic")}</Label>
                <Input
                  id="edit-titlePublic"
                  value={editForm.titlePublic}
                  onChange={(e) => setEditForm({ ...editForm, titlePublic: e.target.value })}
                  placeholder={t("imagesSocial.titlePublicPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-locationTown">{t("imagesSocial.locationTown")}</Label>
                <Input
                  id="edit-locationTown"
                  value={editForm.locationTown}
                  onChange={(e) => setEditForm({ ...editForm, locationTown: e.target.value })}
                  placeholder={t("imagesSocial.locationTownPlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-quantity">{t("imagesSocial.quantity")}</Label>
                <Input
                  id="edit-quantity"
                  type="number"
                  min={1}
                  value={editForm.quantity}
                  onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="edit-socialAllowed"
                  checked={editForm.socialAllowed}
                  onCheckedChange={(checked) =>
                    setEditForm({ ...editForm, socialAllowed: checked === true })
                  }
                />
                <Label htmlFor="edit-socialAllowed" className="font-normal">
                  {t("imagesSocial.socialAllowed")}
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                if (!editForm.name.trim()) {
                  alert("A mappa neve kötelező");
                  return;
                }
                if (!editingFolderId) return;
                let nameToSave = editForm.name.trim();
                if (editForm.category === "realizacio" && editForm.installDate && editForm.dealId) {
                  const dealTitle = Array.isArray(dealsQuery.data) ? dealsQuery.data.find((d) => d.id === editForm.dealId)?.title : null;
                  if (dealTitle) {
                    nameToSave = `${editForm.installDate} ${dealTitle}`;
                  }
                }
                updateFolder.mutate({
                  folderId: editingFolderId,
                  data: {
                    name: nameToSave,
                    panelStructure: editForm.panelStructure || null,
                    color: editForm.color || null,
                    prelisType: editForm.prelisType && editForm.prelisType.trim() ? editForm.prelisType : null,
                    brand: editForm.brand || null,
                    folderType: editForm.folderType || null,
                    model: editForm.model || null,
                    installDate: editForm.installDate || null,
                    dealId: editForm.dealId || null,
                    category: editForm.category || "bemeres",
                    socialCategory: editForm.socialCategory || null,
                    socialAllowed: editForm.socialAllowed,
                    titlePublic: editForm.titlePublic.trim() || null,
                    locationTown: editForm.locationTown.trim() || null,
                    quantity: editForm.quantity.trim() ? Number(editForm.quantity) : null,
                  },
                });
              }}
              disabled={updateFolder.isPending}
            >
              {updateFolder.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("common.loading")}
                </>
              ) : (
                t("common.save")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={imageDropDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeImageDropDialog();
          else setImageDropDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("images.imageDropTitle")}</DialogTitle>
            <DialogDescription>
              {t("images.imageDropDescription", { count: imageDropFiles.length })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {folders.length > 0 && (
              <div className="space-y-2">
                <Label>{t("images.imageDropPickFolder")}</Label>
                <Select value={imageDropFolderId} onValueChange={setImageDropFolderId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("images.imageDropPickFolder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {folders.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name || f.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("images.uploadSubfolderType")}</Label>
              <Select
                value={imageDropSubfolder}
                onValueChange={(v: SubfolderType) => setImageDropSubfolder(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                  <SelectItem value="munkaink">{t("images.munkaink")}</SelectItem>
                  <SelectItem value="marketing">{t("images.marketing")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              className="w-full"
              disabled={folders.length === 0 || !imageDropFolderId || imageDropAction !== null}
              onClick={() => void confirmImageDropToFolder()}
            >
              {imageDropAction === "folder" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("images.uploading")}
                </>
              ) : (
                t("images.imageDropToSelectedFolder")
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={imageDropAction !== null}
              onClick={() => void confirmImageDropToGallery()}
            >
              {imageDropAction === "gallery" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("images.uploading")}
                </>
              ) : (
                t("images.imageDropToGalleryOnly")
              )}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={closeImageDropDialog} disabled={imageDropAction !== null}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("imagesExtra.deleteFolderTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("imagesExtra.deleteFolderDescription", { name: folderToDelete?.name })}
              <br />
              <br />
              {t("imagesExtra.deleteFolderConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteConfirmOpen(false);
              setFolderToDelete(null);
            }}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteFolder.isPending}
            >
              {deleteFolder.isPending ? t("imagesExtra.deleting") : t("images.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobil: szűrő panel (Sheet) */}
      {isMobile && (
        <AppSheet
          open={filterSheetOpen}
          onOpenChange={setFilterSheetOpen}
          title={t("common.filterLabel")}
        >
          <ImageFilters
            filters={filters}
            onFiltersChange={setFilters}
            optionCounts={filterOptionCounts}
            onClose={() => setFilterSheetOpen(false)}
            minDate={dateRangeQuery.data?.minDate}
            maxDate={dateRangeQuery.data?.maxDate}
            dateFilterEnabled={dateFilterEnabled}
            onDateFilterToggle={setDateFilterEnabled}
            hideFiltersTitle
          />
        </AppSheet>
      )}

      {/* Mobil: + gomb jobb alsó sarokban (CRM FAB stílus) */}
      {isMobile && !selectionModeEnabled && (
        <>
          {/* fab-token: unified D */}
          <FabMenu
            alwaysShowMenu
            fabAriaLabel={t("images.newFolder")}
            actions={[
              {
                id: "new-folder",
                label: t("images.newFolder"),
                icon: <Plus className="h-5 w-5" />,
                onClick: handleCreateFolder,
              },
            ]}
          />
        </>
      )}

      {isMobile && selectionModeEnabled && (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
            <Button
              ref={bulkHeaderDismissRef}
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              aria-label={t("images.cancelSelection")}
              onClick={exitFolderSelectionMode}
            >
              <X className="h-4 w-4" />
            </Button>
            <SelectionLiveRegion count={selectedFolders.size} />
            <span className="max-w-[10rem] truncate text-sm text-muted-foreground sm:max-w-none">
              {selectedFolders.size} {t("images.selected")}
            </span>
            {selectedFolders.size === 0 ? (
              <span className="text-sm text-muted-foreground">{t("common.selectItemsHint")}</span>
            ) : null}
            <button
              type="button"
              className="shrink-0 text-sm text-primary underline-offset-4 hover:underline disabled:pointer-events-none disabled:opacity-50 hidden sm:inline"
              disabled={(foldersQuery.data ?? []).length === 0}
              onClick={() => {
                const list = foldersQuery.data ?? [];
                setSelectedFolders(new Set(list.map((f) => f.id)));
              }}
            >
              {t("common.selectAll")}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 w-9 shrink-0 p-0 ml-auto" aria-label={t("common.more")}>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    const list = foldersQuery.data ?? [];
                    setSelectedFolders(new Set(list.map((f) => f.id)));
                  }}
                  disabled={(foldersQuery.data ?? []).length === 0}
                >
                  {t("common.selectAll")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setSocialMarkDate(new Date().toISOString().slice(0, 10));
                    setSocialMarkChannel("both");
                    setSocialMarkOpen(true);
                  }}
                  disabled={selectedFolders.size === 0 || socialBulkPending}
                >
                  {t("imagesSocial.markAsUploaded")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setSocialUnmarkOpen(true)}
                  disabled={selectedFolders.size === 0 || socialBulkPending}
                >
                  {t("imagesSocial.unmarkUploaded")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    const folderIds = Array.from(selectedFolders);
                    for (const folderId of folderIds) {
                      try {
                        const r = await fetch(apiUrl(`/api/images/folders/${folderId}/download`), {
                          credentials: "include",
                        });
                        if (!r.ok) continue;
                        const data = await r.json();
                        data.photos?.forEach((photo: { url: string; fileName: string }) => {
                          window.open(apiUrl(photo.url), "_blank");
                        });
                      } catch {
                        // ignore
                      }
                    }
                  }}
                  disabled={selectedFolders.size === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t("images.downloadLabel")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleBulkDelete}
                  disabled={selectedFolders.size === 0 || bulkDelete.isPending}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("images.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <Dialog open={socialMarkOpen} onOpenChange={setSocialMarkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("imagesSocial.markDialogTitle")}</DialogTitle>
            <DialogDescription>{t("imagesSocial.markDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t("imagesSocial.filterSocial")}</Label>
              <Select
                value={socialMarkChannel}
                onValueChange={(v) =>
                  setSocialMarkChannel(v as "both" | "facebook" | "instagram")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">{t("imagesSocial.channelBoth")}</SelectItem>
                  <SelectItem value="facebook">{t("imagesSocial.channelFacebook")}</SelectItem>
                  <SelectItem value="instagram">{t("imagesSocial.channelInstagram")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="social-mark-date">{t("imagesSocial.postedAtLabel")}</Label>
              <Input
                id="social-mark-date"
                type="date"
                value={socialMarkDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setSocialMarkDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSocialMarkOpen(false)} disabled={socialBulkPending}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSocialMarkSubmit} disabled={socialBulkPending || selectedFolders.size === 0}>
              {socialBulkPending ? t("imagesSocial.markPending") : t("imagesSocial.markSubmit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={socialUnmarkOpen} onOpenChange={setSocialUnmarkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("imagesSocial.unmarkConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("imagesSocial.unmarkConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={socialBulkPending}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSocialUnmarkSubmit} disabled={socialBulkPending}>
              {socialBulkPending ? t("imagesSocial.markPending") : t("imagesSocial.unmarkUploaded")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
